<script setup lang="ts">
import { ref, onMounted, onUnmounted } from "vue";
import { useTranslation } from "i18next-vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

defineProps<{
  diagnosticId?: number;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "recorded", audioBlob: Blob): void;
}>();

const { t } = useTranslation();

type State =
  | "checking"
  | "requestPermission"
  | "permissionDenied"
  | "listening"
  | "processing"
  | "error";

const state = ref<State>("checking");
const errorMessage = ref("");

// MediaRecorder for audio capture
let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let audioStream: MediaStream | null = null;
const recordedAudioBlob = ref<Blob | null>(null);

// Prevent the initial click from stopping recording
let overlayOpenedAt = 0;

const checkPermission = async (): Promise<"granted" | "denied" | "prompt"> => {
  try {
    if (navigator.permissions) {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      return result.state as "granted" | "denied" | "prompt";
    }
    return "prompt";
  } catch {
    return "prompt";
  }
};

const requestMicrophoneAccess = () => {
  startRecording();
};

const startRecording = async () => {
  state.value = "checking";
  try {
    audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    audioChunks = [];
    recordedAudioBlob.value = null;

    mediaRecorder = new MediaRecorder(audioStream, {
      mimeType: MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4",
    });

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunks.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      recordedAudioBlob.value = new Blob(audioChunks, {
        type: mediaRecorder?.mimeType || "audio/webm",
      });
    };

    mediaRecorder.start();
    state.value = "listening";
  } catch (err) {
    console.error("Failed to start recording:", err);
    if (err instanceof DOMException && err.name === "NotAllowedError") {
      state.value = "permissionDenied";
    } else {
      errorMessage.value = t("dashboard:fieldObservations.voice.error");
      state.value = "error";
    }
  }
};

const stopRecording = () => {
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop();
  }
  if (audioStream) {
    audioStream.getTracks().forEach((track) => track.stop());
    audioStream = null;
  }
};

const handleStop = () => {
  state.value = "processing";
  stopRecording();

  setTimeout(() => {
    if (recordedAudioBlob.value) {
      // Émettre directement sans passer par la confirmation
      emit("recorded", recordedAudioBlob.value);
    } else {
      errorMessage.value = t("dashboard:fieldObservations.voice.noSpeech");
      state.value = "error";
    }
  }, 500);
};

const handleCancel = () => {
  emit("close");
};

const handleRetry = () => {
  errorMessage.value = "";
  audioChunks = [];
  recordedAudioBlob.value = null;
  startRecording();
};

const handleOverlayClick = () => {
  if (Date.now() - overlayOpenedAt < 500) {
    return;
  }
  if (state.value === "listening") {
    handleStop();
  }
};

onMounted(async () => {
  overlayOpenedAt = Date.now();

  const permission = await checkPermission();
  if (permission === "granted") {
    startRecording();
  } else {
    state.value = "requestPermission";
  }
});

onUnmounted(() => {
  stopRecording();
});
</script>

<template>
  <div class="voice-overlay" @click="handleOverlayClick">
    <!-- Checking state -->
    <div v-if="state === 'checking'" class="overlay-content">
      <div class="checking-icon">
        <span class="spinner-border" />
      </div>
      <p class="overlay-text">
        {{ t("dashboard:fieldObservations.voice.checking") }}
      </p>
    </div>

    <!-- Request permission state -->
    <div v-else-if="state === 'requestPermission'" class="confirm-dialog">
      <div class="confirm-header">
        <PhosphorIcon name="microphone" :size="24" weight="fill" />
        <span>{{
          t("dashboard:fieldObservations.voice.permissionTitle")
        }}</span>
      </div>
      <div class="confirm-text">
        {{ t("dashboard:fieldObservations.voice.permissionMessage") }}
      </div>
      <div class="confirm-actions">
        <button class="btn-cancel" @click="handleCancel">
          {{ t("cancel") }}
        </button>
        <button class="btn-confirm" @click="requestMicrophoneAccess">
          {{ t("dashboard:fieldObservations.voice.grantAccess") }}
        </button>
      </div>
    </div>

    <!-- Permission denied state -->
    <div v-else-if="state === 'permissionDenied'" class="confirm-dialog">
      <div class="confirm-header error">
        <PhosphorIcon name="microphone-slash" :size="24" weight="fill" />
        <span>{{ t("dashboard:fieldObservations.voice.deniedTitle") }}</span>
      </div>
      <div class="confirm-text">
        {{ t("dashboard:fieldObservations.voice.deniedMessage") }}
      </div>
      <div class="confirm-actions">
        <button class="btn-cancel" @click="handleCancel">
          {{ t("close") }}
        </button>
      </div>
    </div>

    <!-- Listening state -->
    <div v-else-if="state === 'listening'" class="overlay-content">
      <div class="pulse-container">
        <div class="pulse-ring"></div>
        <div class="pulse-ring delay-1"></div>
        <div class="pulse-ring delay-2"></div>
        <div class="mic-circle">
          <PhosphorIcon name="microphone" :size="48" weight="fill" />
        </div>
      </div>
      <p class="overlay-text">
        {{ t("dashboard:fieldObservations.voice.listening") }}
      </p>
      <p class="overlay-hint">
        {{ t("dashboard:fieldObservations.voice.tapToStop") }}
      </p>
    </div>

    <!-- Processing state -->
    <div v-else-if="state === 'processing'" class="overlay-content">
      <div class="checking-icon">
        <span class="spinner-border" />
      </div>
      <p class="overlay-text">
        {{ t("dashboard:fieldObservations.voice.processing") }}
      </p>
    </div>

    <!-- Error state -->
    <div v-else-if="state === 'error'" class="confirm-dialog">
      <div class="confirm-header error">
        <PhosphorIcon name="warning" :size="24" weight="fill" />
        <span>{{ t("error") }}</span>
      </div>
      <div class="confirm-text">
        {{ errorMessage }}
      </div>
      <div class="confirm-actions">
        <button class="btn-cancel" @click="handleCancel">
          {{ t("close") }}
        </button>
        <button class="btn-confirm" @click="handleRetry">
          {{ t("dashboard:fieldObservations.voice.retry") }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// Fullscreen editor for confirm state
.voice-note-editor {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: var(--bs-body-bg);
  display: flex;
  flex-direction: column;
}

.editor-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid var(--bs-border-color);
}

.btn-back {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background-color: var(--bs-tertiary-bg);
  }
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.editor-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  padding-bottom: 100px;
}

.audio-section {
  max-width: 500px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;

  label {
    font-size: 14px;
    font-weight: 600;
    color: var(--bs-body-color);
  }
}

.audio-player-wrapper {
  background-color: var(--el-bg-color);
  border-radius: calc(var(--bs-border-radius) * 2);
  padding: 16px;
  display: flex;
  justify-content: center;
}

.audio-player {
  width: 100%;
  max-width: 400px;
}

.editor-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background-color: var(--bs-body-bg);
  border-top: 1px solid var(--bs-border-color);
}

.btn-save {
  width: 100%;
  padding: 14px 24px;
  border: none;
  border-radius: calc(var(--bs-border-radius) * 2);
  background-color: var(--bs-primary);
  color: white;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

// Standard overlay styles
.voice-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background-color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
}

.overlay-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
}

.pulse-container {
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px solid rgba(239, 68, 68, 0.5);
  animation: pulse 1.5s ease-out infinite;
}

.pulse-ring.delay-1 {
  animation-delay: 0.5s;
}

.pulse-ring.delay-2 {
  animation-delay: 1s;
}

@keyframes pulse {
  0% {
    transform: scale(0.8);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.mic-circle {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  z-index: 1;
}

.checking-icon {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: var(--bs-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;

  .spinner-border {
    width: 48px;
    height: 48px;
    border-width: 4px;
  }
}

.overlay-text {
  color: white;
  font-size: 18px;
  font-weight: 500;
}

.overlay-hint {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin-top: -16px;
}

.confirm-dialog {
  background-color: var(--bs-body-bg);
  border-radius: 16px;
  width: 90%;
  max-width: 400px;
  overflow: hidden;
}

.confirm-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background-color: #ef4444;
  color: white;
  font-size: 18px;
  font-weight: 600;

  i {
    font-size: 24px;
  }

  &.error {
    background-color: #f97316;
  }
}

.confirm-text {
  padding: 20px;
  font-size: 16px;
  line-height: 1.5;
  color: var(--bs-body-color);
  max-height: 200px;
  overflow-y: auto;
}

.confirm-actions {
  display: flex;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid var(--bs-border-color);
}

.btn-cancel,
.btn-confirm {
  flex: 1;
  padding: 12px 20px;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
}

.btn-cancel {
  background-color: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}

.btn-confirm {
  background-color: var(--bs-primary);
  color: white;
}
</style>
